import React from 'react';
import { useIntl } from 'react-intl';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import type { CampaignDetail } from '../outreachTypes';
import { planRate } from './planRates';
import { campaignMessages } from './campaignMessages';
import { startMessages as messages } from './startMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the setup; each part uses its own slice of it */
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StartBarProps {
  campaign: CampaignDetail;
  fitNow: number;
  hasMailbox: boolean;
  readOnly: boolean;
  isStarting: boolean;
  /** A running campaign ("Edit setup"): the button says "Save changes". */
  isEditing?: boolean;
  onBack: () => void;
  onStart: () => void;
}

/**
 * The start bar of step 5 (design outreach-v2 f1, f2): what happens tonight, Back and "Start
 * pitching". Free sees "See plans": the setup is saved and the plan offer opens.
 */
const StartBarComponent: React.FC<StartBarProps> = ({
  campaign,
  fitNow,
  hasMailbox,
  readOnly,
  isStarting,
  isEditing = false,
  onBack,
  onStart,
}) => {
  const intl = useIntl();
  const { plan } = useMusic24Capabilities();
  const openUpgradePrompt = useUpgradePrompt();
  const isFree = campaign.dailyRate === 0;
  const isAutopilot = campaign.mode === 'autopilot';

  const text = (): { title: string; sub: string } => {
    if (isEditing) {
      return { title: intl.formatMessage(messages.barEditing), sub: intl.formatMessage(messages.barEditingSub) };
    }
    if (isFree) {
      return {
        title: intl.formatMessage(messages.barFree),
        sub: intl.formatMessage(messages.barFreeSub, { count: fitNow, song: campaign.songTitle }),
      };
    }
    if (!hasMailbox) {
      return { title: intl.formatMessage(messages.barReview), sub: intl.formatMessage(messages.barNeedsMailbox) };
    }
    if (plan === 'trial') {
      return {
        title: intl.formatMessage(messages.barTrial, { rate: campaign.dailyRate }),
        sub: intl.formatMessage(messages.barTrialSub, {
          artist: planRate('artist'),
          pro: planRate('pro'),
          label: planRate('label'),
        }),
      };
    }
    if (isAutopilot) {
      return {
        title: intl.formatMessage(messages.barAutopilot, { rate: campaign.dailyRate }),
        sub: intl.formatMessage(messages.barAutopilotSub),
      };
    }
    return { title: intl.formatMessage(messages.barReview), sub: intl.formatMessage(messages.barReviewSub) };
  };
  const { title, sub } = text();

  return (
    <div className={styles.bigStart} data-testid="builder-start-bar">
      <div className="flex-grow-1">
        <b>{title}</b>
        <span>{sub}</span>
      </div>
      <SMLButton color="link" omitCap onClick={onBack}>
        {intl.formatMessage(campaignMessages.back)}
      </SMLButton>
      {isEditing ? (
        <SMLButton color="primary" omitCap disabled={readOnly} onClick={onStart} testID="builder-save">
          {intl.formatMessage(messages.saveButton)}
        </SMLButton>
      ) : isFree ? (
        <SMLButton
          color="primary"
          omitCap
          disabled={readOnly}
          onClick={() => openUpgradePrompt(SMLUpgradeFeature.outreach, campaign.songTitle)}
          testID="builder-see-plans"
        >
          {intl.formatMessage(messages.seePlans)}
        </SMLButton>
      ) : (
        <SMLButton
          color="primary"
          omitCap
          leftIcon={faPaperPlane}
          isLoading={isStarting}
          disabled={readOnly || !hasMailbox}
          onClick={onStart}
          testID="builder-start"
        >
          {intl.formatMessage(messages.startButton)}
        </SMLButton>
      )}
    </div>
  );
};

export const StartBar = TypedMemo(StartBarComponent);
